import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import axiosInstance from '../../utils/axiosConfig';
import useTranslation from '../../utils/useTranslation';
import SectionViewAllLink from '../SectionViewAllLink';

const YOUTUBE_ID_RE =
  /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/i;

export function getYouTubeId(url) {
  if (!url) return null;
  const m = String(url).match(YOUTUBE_ID_RE);
  return m ? m[1] : null;
}
export function isYouTubeUrl(url) {
  return !!getYouTubeId(url);
}
export function youTubeEmbedUrl(url) {
  const id = getYouTubeId(url);
  return id ? `https://www.youtube.com/embed/${id}` : '';
}
export function youTubeThumbUrl(url) {
  const id = getYouTubeId(url);
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : '';
}
export function isYouTubeItem(item) {
  if (!item || item.type !== 'video') return false;
  if (item.video_source === 'link') return true;
  return isYouTubeUrl(item.src_url);
}

const PhotoBadgeIcon = (
  <svg className="album-cover-badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="14" height="12" rx="2" />
    <path d="M7 9l2.5 2.5L12 9l3 3" />
    <circle cx="8.5" cy="9" r="0.9" fill="currentColor" stroke="none" />
    <rect x="7" y="3" width="14" height="12" rx="2" />
  </svg>
);

const VideoBadgeIcon = (
  <svg className="album-cover-badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="5" width="14" height="12" rx="2" />
    <path d="m16 10 6-3.5v11L16 14z" />
  </svg>
);

export function MediaThumb({ item }) {
  if (!item) return null;
  if (item.type === 'video') {
    if (isYouTubeItem(item)) {
      return <img src={youTubeThumbUrl(item.src_url)} alt="" loading="lazy" />;
    }
    return <video src={item.src_url} muted playsInline preload="metadata" />;
  }
  return <img src={item.src_url} alt="" loading="lazy" />;
}

export default function GallerySection({
  innerRef,
  limit = 3,
  showHeading = true,
  showViewAll = true,
  filterType = 'all',
  pageMode = false,
}) {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axiosInstance
      .get('public/gallery/', { params: { lang } })
      .then((res) => {
        if (!cancelled) setAlbums([...(res.data?.albums || [])]);
      })
      .catch(() => {
        if (!cancelled) setAlbums([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const filteredAlbums = albums
    .map((album) => {
      const items =
        filterType === 'all'
          ? album.items || []
          : (album.items || []).filter((it) => it.type === filterType);
      return { ...album, items };
    })
    .filter((album) => album.items.length > 0);

  const visibleAlbums = Number.isFinite(limit) ? filteredAlbums.slice(0, limit) : filteredAlbums;
  const hasMore = showViewAll && filteredAlbums.length > visibleAlbums.length;

  const openAlbum = (album) => {
    navigate(`/gallery/${album.id}`);
  };

  // On the home page, hide the whole section when there is nothing to show.
  if (!loading && filteredAlbums.length === 0 && !pageMode) {
    return <section className="section" id="gallery" ref={innerRef} hidden />;
  }

  const body = (
    <>
      {showHeading && (
        <div className="projects-head">
          <h2 className="partners-title">
            <span className="partners-title-line" aria-hidden="true" />
            <span className="partners-title-text">{t('gallery_title')}</span>
            <span className="partners-title-line" aria-hidden="true" />
          </h2>
          {!loading && hasMore && (
            <SectionViewAllLink
              label={t('gallery_view_all')}
              onClick={() => navigate('/gallery')}
            />
          )}
        </div>
      )}

      {loading ? (
        <div className="spinner" />
      ) : visibleAlbums.length === 0 ? (
        <p className="muted-note">{t('gallery_filter_empty')}</p>
      ) : (
        <ul className="album-grid">
          {visibleAlbums.map((album) => {
            const cover = album.items.slice(0, 4);
            const extra = album.items.length - cover.length;
            const photoN = album.items.filter((it) => it.type === 'photo').length;
            const videoN = album.items.filter((it) => it.type === 'video').length;
            return (
              <li key={album.id} className="album-item">
                <button
                  type="button"
                  className="album-card"
                  onClick={() => openAlbum(album)}
                  aria-label={album.title}
                >
                  <span className={`album-cover album-cover--n${cover.length}`}>
                    {(photoN > 0 || videoN > 0) && (
                      <span className="album-cover-badges" aria-hidden="true">
                        {photoN > 0 && (
                          <span className="album-cover-badge">
                            <span className="album-cover-badge-text">
                              {photoN} {t('gallery_photos_label')}
                            </span>
                            {PhotoBadgeIcon}
                          </span>
                        )}
                        {videoN > 0 && (
                          <span className="album-cover-badge">
                            <span className="album-cover-badge-text">
                              {videoN} {t('gallery_videos_label')}
                            </span>
                            {VideoBadgeIcon}
                          </span>
                        )}
                      </span>
                    )}
                    {cover.map((it, i) => (
                      <span key={it.id} className="album-tile-wrap">
                        <MediaThumb item={it} />
                        {it.type === 'video' && (
                          <span className="album-tile-play" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                              <polygon points="6 4 20 12 6 20 6 4" />
                            </svg>
                          </span>
                        )}
                        {i === 3 && extra > 0 && <span className="album-more">+{extra}</span>}
                      </span>
                    ))}
                  </span>
                  <span className="album-info">
                    <span className="album-name">{album.title}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );

  if (pageMode) {
    return (
      <div className="gallery-page-body" ref={innerRef}>
        {body}
      </div>
    );
  }

  return (
    <section className="section" id="gallery" ref={innerRef}>
      <div className="container">
        {body}
      </div>
    </section>
  );
}
